import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CadastralParcelDto, CadastralParcelFeatureDto, PropertyCadastralDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap } from 'rxjs';
import { PropertyCadastralMapComponent } from '../../property-geospatial/cards/cadastral-card/cadastral-map.component';

@Component({
  selector: 'etxe-property-cadastral-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    PropertyCadastralMapComponent,
  ],
  templateUrl: './property-cadastral-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCadastralStepComponent {
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  // Inputs
  dto = input<PropertyCadastralDto | null | undefined>();
  addressLabel = input<string | null | undefined>();
  cityCode = input<string | null | undefined>();
  coordinates = input<{ latitude?: number; longitude?: number } | null | undefined>();
  isConfirmed = input<boolean>(false);
  isLocked = input<boolean>(false);

  // Outputs
  dtoChanged = output<PropertyCadastralDto>();
  confirmationChanged = output<boolean>();

  // Local state for parcel
  private localCadastral = signal<PropertyCadastralDto | null>(null);

  effectiveCadastral = computed(() => this.localCadastral() ?? this.dto());

  parcels = computed<CadastralParcelDto[]>(() => this.effectiveCadastral()?.parcels ?? []);

  totalSurface = computed<number>(() => {
    const declared = this.effectiveCadastral()?.totalSurface;
    if (declared != null) return declared;
    return this.parcels().reduce((acc, p) => acc + (p.surface || 0), 0);
  });

  primaryParcel = computed<CadastralParcelDto | null>(() => {
    const list = this.parcels();
    return list.find((p) => p.isPrimary) ?? list[0] ?? null;
  });

  selectedReferences = computed<string[]>(() => this.parcels().map((p) => p.reference));

  hasData = computed(() => this.parcels().length > 0);

  hasBuiltParcel = computed(() => this.parcels().some((p) => p.hasBdnbBuilding));

  hasCoordinates = computed(() =>
    Boolean(this.coordinates()?.latitude != null && this.coordinates()?.longitude != null),
  );

  // Manual edition state
  isEditingManual = signal<boolean>(false);
  manualReference = signal<string>('');
  manualSurface = signal<number | null>(null);
  isResolvingParcel = signal<boolean>(false);
  resolvedParcel = signal<CadastralParcelFeatureDto | null>(null);
  resolutionStatus = signal<'idle' | 'searching' | 'found' | 'not_found'>('idle');

  private readonly referenceChange$ = new Subject<string>();

  constructor() {
    // Real-time parcel search by reference/lot number
    this.referenceChange$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((ref) => {
          const clean = ref.trim();
          if (!clean) {
            this.manualSurface.set(null);
            this.resolvedParcel.set(null);
            this.resolutionStatus.set('idle');
            this.isResolvingParcel.set(false);
            return of(null);
          }

          this.isResolvingParcel.set(true);
          this.resolutionStatus.set('searching');

          const currentRef = this.primaryParcel()?.reference;
          const effectiveCityCode =
            this.cityCode()?.trim() ||
            (currentRef && currentRef.length === 14 ? currentRef.substring(0, 5) : undefined);

          const lat = this.coordinates()?.latitude;
          const lng = this.coordinates()?.longitude;

          return this.apiService
            .business()
            .etxe()
            .public()
            .gouv()
            .cadastreParcelByRef(clean, effectiveCityCode, lat, lng)
            .pipe(
              catchError(() => of(null)),
              finalize(() => this.isResolvingParcel.set(false)),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((parcel) => {
        if (parcel && parcel.surface != null) {
          this.resolvedParcel.set(parcel);
          this.manualSurface.set(parcel.surface);
          this.resolutionStatus.set('found');
        } else {
          this.resolvedParcel.set(null);
          this.manualSurface.set(null);
          this.resolutionStatus.set('not_found');
        }
      });
  }

  onManualReferenceInput(newRef: string): void {
    this.manualReference.set(newRef);
    this.referenceChange$.next(newRef);
  }

  onParcelSelected(parcel: CadastralParcelFeatureDto): void {
    if (this.isLocked()) return;

    const currentList = [...this.parcels()];
    const index = currentList.findIndex(
      (p) =>
        p.reference.toUpperCase() === parcel.id.toUpperCase() ||
        (p.section.toUpperCase() === parcel.section.toUpperCase() &&
          p.numero.toUpperCase() === parcel.numero.toUpperCase()),
    );

    let updatedList: CadastralParcelDto[];

    if (index >= 0) {
      // Toggle off / remove parcel
      const removed = currentList[index];
      updatedList = currentList.filter((_, i) => i !== index);

      // If removed parcel was primary, designate new primary
      if (removed.isPrimary && updatedList.length > 0) {
        const built = updatedList.find((p) => p.hasBdnbBuilding);
        if (built) {
          built.isPrimary = true;
        } else {
          updatedList[0].isPrimary = true;
        }
      }
    } else {
      // Add parcel
      const isFirst = currentList.length === 0;
      const shouldBePrimary =
        isFirst ||
        (Boolean(parcel.hasBdnbBuilding) && !currentList.some((p) => p.isPrimary && p.hasBdnbBuilding));

      if (shouldBePrimary) {
        currentList.forEach((p) => (p.isPrimary = false));
      }

      const newParcel: CadastralParcelDto = {
        reference: parcel.id,
        section: parcel.section,
        numero: parcel.numero,
        surface: parcel.surface,
        isPrimary: shouldBePrimary,
        hasBdnbBuilding: parcel.hasBdnbBuilding,
      };

      updatedList = [...currentList, newParcel];
    }

    const totalSurface = updatedList.reduce((sum, p) => sum + (p.surface || 0), 0);
    const newDto: PropertyCadastralDto = {
      parcels: updatedList,
      totalSurface: totalSurface > 0 ? totalSurface : undefined,
    };

    this.localCadastral.set(newDto);
    this.dtoChanged.emit(newDto);
  }

  removeParcel(reference: string): void {
    if (this.isLocked()) return;
    const currentList = this.parcels().filter((p) => p.reference !== reference);
    if (currentList.length > 0 && !currentList.some((p) => p.isPrimary)) {
      currentList[0].isPrimary = true;
    }
    const totalSurface = currentList.reduce((sum, p) => sum + (p.surface || 0), 0);
    const newDto: PropertyCadastralDto = {
      parcels: currentList,
      totalSurface: totalSurface > 0 ? totalSurface : undefined,
    };
    this.localCadastral.set(newDto);
    this.dtoChanged.emit(newDto);
  }

  canBePrimary(parcel: CadastralParcelDto): boolean {
    if (parcel.hasBdnbBuilding) return true;
    // Si aucune parcelle bâtie n'est sélectionnée (ex: terrain nu), n'importe quelle parcelle peut être principale
    if (!this.hasBuiltParcel()) return true;
    // Si au moins une parcelle bâtie est sélectionnée, une parcelle non bâtie ne peut pas être principale
    return false;
  }

  setPrimaryParcel(reference: string): void {
    if (this.isLocked()) return;
    const target = this.parcels().find((p) => p.reference === reference);
    if (!target || !this.canBePrimary(target)) return;

    const currentList = this.parcels().map((p) => ({
      ...p,
      isPrimary: p.reference === reference,
    }));
    const totalSurface = currentList.reduce((sum, p) => sum + (p.surface || 0), 0);
    const newDto: PropertyCadastralDto = {
      parcels: currentList,
      totalSurface: totalSurface > 0 ? totalSurface : undefined,
    };
    this.localCadastral.set(newDto);
    this.dtoChanged.emit(newDto);
  }

  startManualEdit(): void {
    if (this.isLocked()) return;
    const primary = this.primaryParcel();
    const currentRef = primary?.reference ?? '';
    const currentSurf = primary?.surface ?? null;
    this.manualReference.set(currentRef);
    this.manualSurface.set(currentSurf);
    this.resolvedParcel.set(null);
    this.resolutionStatus.set(currentSurf != null ? 'found' : 'idle');
    this.isEditingManual.set(true);
  }

  cancelManualEdit(): void {
    this.isEditingManual.set(false);
    this.resolvedParcel.set(null);
    this.resolutionStatus.set('idle');
  }

  saveManualEdit(): void {
    const resolved = this.resolvedParcel();
    const ref = resolved?.id || this.manualReference().trim().toUpperCase();
    if (!ref) return;

    const surface = resolved?.surface ?? this.manualSurface() ?? undefined;
    const section =
      resolved?.section || (ref.length >= 10 ? ref.substring(ref.length - 6, ref.length - 4) : '');
    const numero = resolved?.numero || (ref.length >= 10 ? ref.substring(ref.length - 4) : ref);

    const currentList = [...this.parcels()];
    const existingIndex = currentList.findIndex((p) => p.reference.toUpperCase() === ref.toUpperCase());

    if (existingIndex >= 0) {
      this.isEditingManual.set(false);
      return;
    }

    const isFirst = currentList.length === 0;
    const hasBdnb = resolved?.hasBdnbBuilding;
    const shouldBePrimary = isFirst || (Boolean(hasBdnb) && !currentList.some((p) => p.isPrimary));

    if (shouldBePrimary) {
      currentList.forEach((p) => (p.isPrimary = false));
    }

    const newParcel: CadastralParcelDto = {
      reference: ref,
      section,
      numero,
      surface,
      isPrimary: shouldBePrimary,
      hasBdnbBuilding: hasBdnb,
    };

    const updatedList = [...currentList, newParcel];
    const totalSurface = updatedList.reduce((sum, p) => sum + (p.surface || 0), 0);
    const newDto: PropertyCadastralDto = {
      parcels: updatedList,
      totalSurface: totalSurface > 0 ? totalSurface : undefined,
    };

    this.localCadastral.set(newDto);
    this.dtoChanged.emit(newDto);
    this.isEditingManual.set(false);
  }

  onConfirmationToggle(checked: boolean): void {
    this.confirmationChanged.emit(checked);
  }
}
